import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/products/{code}/base';
export type MakeGetProductBaseByCodeDeps<ProductBase extends SchemaType.Top> = {
  readonly schemas: {
    readonly productBase: ProductBase;
  };
  readonly adapters: {
    readonly productBase: {
      readonly name: (product: ProductBase['Type']) => string | null;
    };
  };
  readonly rawUrlEncode: (input: string) => string;
};

export const makeGetProductBaseByCode = <ProductBase extends SchemaType.Top>(
  deps: MakeGetProductBaseByCodeDeps<ProductBase>
) => {
  const GetProductBaseByCodeError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetBaseByCode',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductBaseByCodeFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.code': code,
        },
      })
    );

  const getProductBaseByCode = (code: string) =>
    getProductBaseByCodeFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.schemas.productBase)(response),
          404: (response) =>
            Effect.fail(new GetProductBaseByCodeError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(new GetProductBaseByCodeError.ApiRequestError({ code, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGetBase,
          'Debug',
          `Successfully resolved product for code: ${code} -> name: ${deps.adapters.productBase.name(result) ?? 'default'}`
        )
      ),
      Effect.withSpan('api.product.getBase', {
        kind: 'internal',
        attributes: {
          'product.code': code,
        },
      })
    );

  return { getProductBaseByCode, GetProductBaseByCodeError };
};
